import React, { useState } from 'react';
import { PlusCircle, Eye, ScanText, CheckCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const ICONS = ['🤖','⚖️','📜','🔍','🧠','💼','📋','🏛️','👨‍⚖️','🛡️','📚','🎯','💡','🔬','🗂️'];

const CORE_API = process.env.REACT_APP_CORE_API || 'https://vidhiguruapi.vidhiguru.in';

const CreateAgentPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState<any>(null);

  const [form, setForm] = useState({
    id: '',
    name: '',
    description: '',
    customInstructions: '',
    ocrEnabled: false,
    icon: '🤖',
    defaultLanguage: 'English',
    capabilities: [] as string[],
  });

  const autoId = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.id || !form.name || !form.customInstructions) {
      toast.error('Please fill in ID, Name, and System Prompt');
      return;
    }
    setLoading(true);
    setCreated(null);
    try {
      const res = await fetch(`${CORE_API}/api/agents/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setCreated(data);
        toast.success(`Agent "${form.name}" created!`);
        setForm({ id: '', name: '', description: '', customInstructions: '', ocrEnabled: false, icon: '🤖', defaultLanguage: 'English', capabilities: [] });
      } else {
        toast.error(data.error || data.message || 'Failed to create agent');
      }
    } catch (err: any) {
      toast.error('API error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCapability = (cap: string) => {
    setForm(f => ({
      ...f,
      capabilities: f.capabilities.includes(cap)
        ? f.capabilities.filter(c => c !== cap)
        : [...f.capabilities, cap],
    }));
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-3xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <PlusCircle className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Agent</h1>
            <p className="text-sm text-gray-500">Configure a new AI agent with a custom system prompt</p>
          </div>
        </div>

        {/* Success Card */}
        {created && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="text-green-600" size={18} />
              <span className="font-semibold text-green-800">Agent Created Successfully</span>
            </div>
            <div className="text-sm text-green-700 space-y-1">
              <p>ID: <code className="bg-green-100 px-1 rounded">{created.agent?.id}</code></p>
              <p>Instructions: {created.instructionsLength?.toLocaleString()} chars</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Basic Information</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => {
                    const name = e.target.value;
                    setForm(f => ({ ...f, name, id: f.id === autoId(f.name) || !f.id ? autoId(name) : f.id }));
                  }}
                  placeholder="My Legal Agent"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID *</label>
                <input
                  type="text"
                  value={form.id}
                  onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
                  placeholder="my-legal-agent"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="A brief description of what this agent does"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(ic => (
                    <button
                      key={ic}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, icon: ic }))}
                      className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center border-2 transition-all ${
                        form.icon === ic ? 'border-indigo-500 bg-indigo-50 scale-110' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Language</label>
                <select
                  value={form.defaultLanguage}
                  onChange={e => setForm(f => ({ ...f, defaultLanguage: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                >
                  {['English', 'Hindi', 'Marathi', 'Gujarati', 'Tamil', 'Telugu', 'Bengali', 'Kannada'].map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Custom Instructions */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <Eye size={14} /> System Prompt *
            </h2>
            <p className="text-xs text-gray-500">
              Define your agent's persona and behavior through the system prompt.
            </p>
            <textarea
              value={form.customInstructions}
              onChange={e => setForm(f => ({ ...f, customInstructions: e.target.value }))}
              rows={10}
              placeholder="Enter your agent's system prompt / instructions here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono resize-y"
            />
            <div className="text-xs text-gray-400 text-right">{form.customInstructions.length} characters</div>
          </div>

          {/* Capabilities */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Capabilities</h2>
            <div className="flex flex-wrap gap-3">
              {/* OCR Toggle */}
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, ocrEnabled: !f.ocrEnabled }))}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all text-sm ${
                  form.ocrEnabled
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <ScanText size={16} />
                OCR Capability
                {form.ocrEnabled && <CheckCircle size={14} className="text-indigo-600" />}
              </button>
              {['legal-analysis', 'document-drafting', 'case-research', 'contract-review', 'voice'].map(cap => (
                <button
                  key={cap}
                  type="button"
                  onClick={() => toggleCapability(cap)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all text-sm ${
                    form.capabilities.includes(cap)
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {cap.replace(/-/g, ' ')}
                  {form.capabilities.includes(cap) && <CheckCircle size={14} className="text-indigo-600" />}
                </button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !form.id || !form.name || !form.customInstructions}
            className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold text-sm hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Creating Agent...</>
            ) : (
              <><PlusCircle size={18} /> Create Agent</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateAgentPage;
